/**
 * Hàm thuần của hai màn cấu hình eKYC. Không chép luật của máy chủ: luật bước và chính sách là của
 * `modules/ekyc/backend/src/quy-trinh/`, màn chỉ đọc kết quả xem trước. Ở đây chỉ có phần **trình bày**: gom, đếm,
 * nối lỗi vào đúng ô.
 */
import type { BuocHieuLuc, NhomBuoc, UngDung, VanDeCauHinh, YeuToRuiRo } from '../types';

/** Lỗi theo con trỏ JSON (`#/chinhSach/weights/pad` → câu lỗi). Chỉ lỗi; cảnh báo không gắn vào ô nào. */
export function loiTheoTruong(vanDe: readonly VanDeCauHinh[]): Record<string, string> {
  const ra: Record<string, string> = {};
  for (const v of vanDe) if (v.muc === 'loi' && !(v.truong in ra)) ra[v.truong] = v.thongDiep;
  return ra;
}

/** Lỗi 400 của máy chủ (`errors[]` của Problem Details) về cùng dạng với lỗi xem trước. */
export function loiTuApi(details: unknown): VanDeCauHinh[] {
  if (!Array.isArray(details)) return [];
  return details
    .filter((d): d is { pointer: string; detail: string } =>
      typeof d === 'object' && d !== null && typeof (d as { pointer?: unknown }).pointer === 'string')
    .map((d) => ({ muc: 'loi', truong: d.pointer, ma: 'MAY_CHU', thongDiep: String(d.detail) }));
}

/**
 * Lỗi không có ô nào trên màn để đứng cạnh — gom vào dải chung. `coO` trả `true` cho con trỏ đã có ô hiện lỗi.
 */
export function loiKhongCoO(vanDe: readonly VanDeCauHinh[], coO: (truong: string) => boolean): VanDeCauHinh[] {
  return vanDe.filter((v) => v.muc === 'loi' && !coO(v.truong));
}

/** Tổng trọng số, làm tròn 3 chữ số để `0.1 + 0.2` không hiện thành `0.30000000000000004`. */
export function tongTrongSo(weights: Readonly<Record<YeuToRuiRo, number>>): number {
  const tong = Object.values(weights).reduce((s, w) => s + (Number.isFinite(w) ? w : 0), 0);
  return Math.round(tong * 1000) / 1000;
}

/** Gom bản hiệu lực theo nhóm, giữ thứ tự của máy chủ — thứ tự ấy là thứ tự chạy thật. */
export function gomTheoNhom(ds: readonly BuocHieuLuc[], nhom: readonly NhomBuoc[]): { nhom: NhomBuoc; buoc: BuocHieuLuc[] }[] {
  return nhom.map((n) => ({ nhom: n, buoc: ds.filter((b) => b.nhom === n) })).filter((g) => g.buoc.length > 0);
}

/**
 * Bước đổi được bằng tay không: không với bước khoá, và không với bước chưa khả dụng — bước ấy không chạy dù bật hay
 * tắt, nên một công tắc ở đó chỉ tạo ảo giác đã cấu hình được nó (`08` §6).
 */
export function batTatDuoc(b: BuocHieuLuc): boolean {
  return !b.khoa && b.khaDung;
}

/**
 * Đặt ý muốn bật/tắt một bước vào delta. Không tự rút gọn — máy chủ giữ bản tối giản khi lưu; ở đây giữ đúng ý
 * người bấm để bấm qua lại không làm mất gì.
 */
export function datBuoc(delta: Readonly<Record<string, boolean>>, ma: string, bat: boolean): Record<string, boolean> {
  return { ...delta, [ma]: bat };
}

/** Hai cấu hình giống nhau không — để biết có gì chưa lưu. So theo nội dung, không theo tham chiếu. */
export function giongNhau(a: unknown, b: unknown): boolean {
  return JSON.stringify(sapKhoa(a)) === JSON.stringify(sapKhoa(b));
}

function sapKhoa(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sapKhoa);
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.keys(v).sort().map((k) => [k, sapKhoa((v as Record<string, unknown>)[k])]));
  }
  return v;
}

export type TinhTrangBiMat = 'chua-cap' | 'da-cap';

/** `phienBanBiMat = 0`: chưa từng cấp, nên chưa ai giữ bí mật để kiểm chữ ký — màn phải mời cấp, không mời "xoay". */
export function tinhTrangBiMat(u: Pick<UngDung, 'phienBanBiMat'>): TinhTrangBiMat {
  return u.phienBanBiMat > 0 ? 'da-cap' : 'chua-cap';
}

/**
 * Chính sách sau khi đổi một trường số mà người dùng còn đang gõ: rỗng hay không phải số thì giữ `NaN` để máy chủ
 * báo lỗi đúng ô, không lặng lẽ thay bằng 0.
 */
export function docSo(chu: string): number {
  const t = chu.trim().replace(',', '.');
  return t === '' ? Number.NaN : Number(t);
}
